import './ScrollingTable.css'
import {CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState} from "react";

interface ChangeData {
    key: ReactNode,
    oldData?: Record<string, ReactNode>
}

export interface ColumnDescription {
    name: string,
    field: string,
    width?: string
}

// invariant that colorCol points to a column that is a pair of strings
function TableRow({data, changeData, keyCol, colorCol, classCol, columns, collapse} : {
    data: Record<string, ReactNode>,
    changeData?: ChangeData,
    keyCol: string,
    colorCol?: string,
    classCol?: string,
    columns: ColumnDescription[],
    collapse?: boolean
}) {
    let bgColor = colorCol && data[colorCol] ? (data[colorCol] as [string, string])[0] : undefined;
    let textColor = colorCol && data[colorCol] ? (data[colorCol] as [string, string])[1] : undefined;
    let className = classCol && data[classCol] as string;
    // Highlight the whole row if it is new or if the first column (the user-perceived key) has changed
    if(changeData && !changeData.oldData) {
        bgColor = 'yellow';
        textColor = 'red';
    }
    return <tr style={{
        background: bgColor,
        color: textColor,
        visibility: collapse ? 'collapse' : 'visible'
    }} className={className || ''}>
        {columns.map((col) => {
            let overrideColor = changeData && changeData.oldData && changeData.oldData[col.field] !== data[col.field];
            if(col.field === keyCol && changeData) {
                overrideColor = true;
            }
            const style : CSSProperties = {width: col.width};
            if(overrideColor) {
                style.background = 'yellow';
                style.color = 'red';
            }
            return <td style={style} key={col.field}>{data[col.field]}</td>;
        })}
    </tr>
}

function TableBody({data, keyCol, colorCol, classCol, columns, copies, changedRows, collapseRows} : {
    keyCol: string,
    colorCol?: string,
    classCol?: string,
    columns: ColumnDescription[],
    data: Record<string, ReactNode>[],
    copies: number,
    changedRows: ChangeData[],
    collapseRows?: number[]
}) {
    const indexes = [...Array(copies).keys()]
    return <tbody>
    {indexes.map((idx) => {
        const rows = data.map((row, ridx) => <TableRow data={row}
                                                 changeData={changedRows.find(r => r.key === row[keyCol])}
                                                 key={row[keyCol] + '-' + idx}
                                                 colorCol={colorCol} keyCol={keyCol} classCol={classCol}
                                                 columns={columns}
                                                 collapse={collapseRows?.includes(ridx)}/>)
        if (copies > 1) {
            rows.push(<tr key={'sep-' + idx} className="scrollingTableSeparator">
                <td colSpan={columns.length}/>
            </tr>)
        }
        return rows
    })}
    </tbody>
}

export default function ScrollingTable({keyCol, colorCol, classCol, columns, data, disableScroll, width, height, highlightChanges, noHeading, rowSpacing, stickyFooter} : {
    keyCol: string,
    colorCol?: string,
    classCol?: string,
    columns: ColumnDescription[],
    data: Record<string, ReactNode>[],
    disableScroll?: boolean,
    width?: string,
    height: string,
    highlightChanges?: boolean,
    noHeading?: boolean,
    rowSpacing?: number,
    stickyFooter?: number
}) {
    const tableRef = useRef<HTMLDivElement>(null);
    const scrollingTableRef = useRef<HTMLTableElement>(null);
    const oldData = useRef<Record<string, ReactNode>[]>();
    const clearChangedTimeout = useRef<number | null>(null);
    const [isTooTall, setTooTall] = useState(false);
    const [changedRows, setChangedRows] = useState<ChangeData[]>([]);
    const [headHeight, setHeadHeight] = useState('1.4em');
    const [footHeight, setFootHeight] = useState(stickyFooter ? `${stickyFooter * 1.4}em` : '0');
    const [contentHeight, setContentHeight] = useState(height);

    useEffect(() => {
        const el = tableRef.current
        if(!el) {
            return
        }
        setTooTall(el.scrollHeight > el.clientHeight && !disableScroll)
        if(!noHeading) {
            const thead = el.getElementsByTagName('thead')[0]
            const style = getComputedStyle(thead)
            const additionalHeight = style.borderCollapse == 'separate' ? (style.borderSpacing.includes(" ") ? style.borderSpacing.split(" ")[1] : style.borderSpacing) : "0"
            setHeadHeight(`calc(${additionalHeight} + ${thead.scrollHeight}px)`);
        } else {
            setHeadHeight('0px');
        }
        if(stickyFooter) {
            const rows = [...el.getElementsByTagName('tr')]
            const footerRows = rows.slice(Math.max(0, rows.length - stickyFooter))
            const thead = [0]
            const heights: string[] = []
            footerRows.forEach(row => {
                const style = getComputedStyle(row)
                const additionalHeight = style.borderCollapse == 'separate' ? (style.borderSpacing.includes(" ") ? style.borderSpacing.split(" ")[1] : style.borderSpacing) : "0"
                heights.push(additionalHeight)
                heights.push(`${row.scrollHeight}px`)
            })
            setFootHeight(heights.length > 0 ? `calc(${heights.join(" + ")})` : '0');
        } else {
            setFootHeight('0px');
        }
    }, [data, rowSpacing, disableScroll, noHeading]);

    // Separate effect so that isTooTall flips are rendered before we calculate the content height
    useLayoutEffect(() => {
        const el = scrollingTableRef.current
        if(!el) {
            return
        }
        const tbody = el.getElementsByTagName("tbody")[0];
        const firstTop: HTMLTableRowElement = tbody.getElementsByTagName("tr")[0];
        const secondTop: HTMLTableRowElement = (tbody.getElementsByClassName("scrollingTableSeparator")[0] as HTMLTableRowElement).nextSibling as HTMLTableRowElement
        setContentHeight("-" + (secondTop.offsetTop - firstTop.offsetTop) + "px")
    }, [data, rowSpacing, disableScroll, noHeading, isTooTall]);

    // stringify the data parameter so if the dataset hasn't changed at all and we just got a new object, we don't re-calculate diffs
    useEffect(() => {
        setChangedRows(data.filter((row) => {
            if(!oldData.current || oldData.current.length === 0) {
                return false;
            }
            const oldRow = oldData.current.find((r) => r[keyCol] === row[keyCol])
            if(oldRow) {
                return columns.some((c) => oldRow[c.field] !== row[c.field]);
            } else {
                return true;
            }
        }).map(row => ({
            key: row[keyCol],
            oldData: (oldData.current && oldData.current.find((r) => r[keyCol] === row[keyCol]))
        })))
        oldData.current = data;

        clearChangedTimeout.current = window.setTimeout(() => { setChangedRows([])}, 2500)

        return function () {
            if(clearChangedTimeout.current) {
                clearTimeout(clearChangedTimeout.current);
            }
        }
    }, [data]);

    const scrollSpeed = tableRef.current ? tableRef.current.scrollHeight * 0.03 : 0

    const heading = <thead>
        <tr>
            {columns.map((col) => <th style={{width: col.width}} key={col.field}>{col.name}</th>)}
        </tr>
    </thead>

    // This needs a copy of the body so that the column widths appear correctly
    const specialHeading =  noHeading ? null : <div className="scrollingTableFixedHeading">
        <table style={{width: width}}>
            {heading}
            <TableBody copies={1} data={data} columns={columns} keyCol={keyCol} colorCol={colorCol} classCol={classCol} changedRows={highlightChanges ? changedRows : []} />
        </table>
    </div>

    const specialFooter =  !stickyFooter ? null : <div className="scrollingTableFixedFooter">
        <table style={{width: width}}>
            {noHeading ? null : heading}
            <TableBody copies={1} data={data} columns={columns} keyCol={keyCol} colorCol={colorCol} classCol={classCol} changedRows={highlightChanges ? changedRows : []} collapseRows={data.map((_, idx) => idx).slice(0, Math.max(0, data.length - stickyFooter))} />
        </table>
    </div>

    const normalTable = <div className="scrollingTableWrapper" style={{minWidth: width, height: height}} ref={tableRef}>
        <table style={{width: width}}>
            {noHeading ? null : heading}
            <TableBody copies={1} data={data} columns={columns} keyCol={keyCol} colorCol={colorCol} classCol={classCol} changedRows={highlightChanges ? changedRows : []} />
        </table>
    </div>

    return isTooTall ? <div style={{minWidth: width, height: height, '--head-height': headHeight, '--foot-height': footHeight}} className={`scrollingTableWrapperDiv scrollingTableWrapper ${specialFooter ? 'withFooter' : ''}`} >
        {specialHeading}
        <div className={"bodyWrapper"}>
            <div style={{animation: `tableScroll ${scrollSpeed}s linear infinite`, '--end-translation': contentHeight}} ref={scrollingTableRef}>
                <table style={{width: width}}>
                    {noHeading ? null : heading}
                    <TableBody copies={2} data={data} columns={columns} keyCol={keyCol} colorCol={colorCol} classCol={classCol} changedRows={highlightChanges ? changedRows : []} collapseRows={data.map((_, idx) => idx).slice(Math.max(0, data.length - (stickyFooter || 0)))} />
                    {noHeading ? null : heading}
                </table>
            </div>
        </div>
        {specialFooter}
        {normalTable}
    </div> : normalTable
}
